import { Camera, ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface StreetLevelCardProps {
  streetViewUrl: string | null | undefined;
  mapillaryUrl: string | null | undefined;
}

/**
 * What the street looks like at the pin, from the two archives that have
 * driven past it. Links, deliberately: Google's imagery may not be stored,
 * Mapillary's URLs expire, and a scout deciding whether to drive out wants
 * the live view, not a copy. The Mapillary link opens the exact image the
 * exporter chose as the venue's facade when it found one.
 */
export function StreetLevelCard({ streetViewUrl, mapillaryUrl }: StreetLevelCardProps): JSX.Element | null {
  if (!streetViewUrl && !mapillaryUrl) return null;
  return (
    <Card data-testid="street-level-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Street level
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {streetViewUrl && (
          <Button asChild variant="outline" size="sm">
            <a href={streetViewUrl} target="_blank" rel="noopener noreferrer" data-testid="street-view-link">
              <ExternalLink className="mr-2 h-4 w-4" />
              Google Street View
            </a>
          </Button>
        )}
        {mapillaryUrl && (
          <Button asChild variant="outline" size="sm">
            <a href={mapillaryUrl} target="_blank" rel="noopener noreferrer" data-testid="mapillary-link">
              <ExternalLink className="mr-2 h-4 w-4" />
              {mapillaryUrl.includes('pKey=') ? 'Mapillary facade' : 'Mapillary'}
            </a>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
